import React, { useEffect, useState, useMemo, useCallback } from 'react';

import { PlusOutlined, EditOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { Card, Table, Button, Modal, Form, Input, Switch, Tag, Tooltip, message } from 'antd';
import { useTranslation } from 'react-i18next';

import ListPageHeader from '@shared/ListPageHeader';

import { useFeatureEnabled } from '@contexts/operator/PlanContext';
import { shippingTypeService } from '@services/operator/shippingTypeService';

const SOURCE_TAG_MAP = {
  system: 'blue',
  platform: 'purple',
  custom: 'green',
  none: 'default',
};

const ShippingTypesList = () => {
  const { t } = useTranslation();
  const canCreateCustom = useFeatureEnabled('shipping_types.custom');

  // 主列表
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [keyword, setKeyword] = useState('');

  // 新增/編輯自訂物流 Modal
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const CUSTOM_PREFIX = 'custom_';

  // 翻譯後端回傳的 error code。優先用第一個 field error（"欄位: 錯誤"），否則 fallback 到 top-level message。
  const translateApiError = (result) => {
    const fieldErrors = result.errors;
    if (fieldErrors && typeof fieldErrors === 'object') {
      const firstField = Object.keys(fieldErrors)[0];
      const firstDetail = firstField && fieldErrors[firstField]?.[0];
      if (firstDetail) {
        const code = typeof firstDetail === 'object' ? firstDetail.message : firstDetail;
        const interpolations = (typeof firstDetail === 'object' && firstDetail.interpolations) || {};
        const fieldLabel = t(`models.shipping_type.${firstField}`, { defaultValue: firstField });
        const errorLabel = t(`common.errors.messages.${code}`, { ...interpolations, defaultValue: code });
        return `${fieldLabel}: ${errorLabel}`;
      }
    }
    const code = result.message;
    return code
      ? t(`common.error.${code}`, { defaultValue: t('common.error.operation_failed') })
      : t('common.error.operation_failed');
  };

  // ── Data Fetching ──

  const fetchData = useCallback(async (searchKeyword = '') => {
    setLoading(true);
    const result = await shippingTypeService.index({ keyword: searchKeyword });
    if (result.success) {
      setData(result.data);
    } else {
      message.error(translateApiError(result));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Main List Handlers ──

  const handleSearch = (value) => {
    setKeyword(value);
    fetchData(value);
  };

  const handleToggleEnable = async (record) => {
    const result = await shippingTypeService.update(record.id, { enable: !record.enable });
    if (result.success) {
      message.success(t('common.message.update_success'));
      fetchData(keyword);
    } else {
      message.error(translateApiError(result));
    }
  };

  // ── Custom Type Modal Handlers ──

  const handleAddCustom = () => {
    if (!canCreateCustom) return;
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({ enable: true });
    setIsModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      name: record.name,
      enable: record.enable,
    });
    setIsModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setEditingRecord(null);
    form.resetFields();
  };

  const handleModalSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      let result;

      if (editingRecord) {
        result = await shippingTypeService.update(editingRecord.id, { name: values.name });
      } else {
        result = await shippingTypeService.create(values);
      }

      if (result.success) {
        message.success(editingRecord ? t('common.message.update_success') : t('common.message.create_success'));
        handleModalCancel();
        fetchData(keyword);
      } else {
        message.error(translateApiError(result));
      }
    } catch (error) {
      // Validate failed
    } finally {
      setSaving(false);
    }
  };

  // ── Table Column Definitions ──

  const columns = useMemo(() => [
    {
      title: t('models.shipping_type.code'),
      dataIndex: 'code',
      key: 'code',
      width: 180,
    },
    {
      title: t('models.shipping_type.name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('models.shipping_type.source'),
      dataIndex: 'source',
      key: 'source',
      width: 120,
      render: (source) => (
        <Tag color={SOURCE_TAG_MAP[source] || 'default'}>
          {t(`enums.shipping_type.source.${source}`, source)}
        </Tag>
      )
    },
    {
      title: t('models.shipping_type.provider'),
      dataIndex: 'provider',
      key: 'provider',
      width: 120,
      render: (provider) => provider ? t(`enums.shipping_type.provider.${provider}`, provider) : null,
    },
    {
      title: t('operator.pages.master_data.shipping_types.carrier'),
      dataIndex: 'carrier',
      key: 'carrier',
      width: 120,
      render: (carrier) => carrier ? t(`enums.shipping_type.carrier.${carrier}`, carrier) : null,
    },
    {
      title: t('operator.pages.master_data.shipping_types.supports_cod'),
      dataIndex: 'supports_cod',
      key: 'supports_cod',
      width: 80,
      align: 'center',
      render: (val) => val ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : null
    },
    {
      title: t('models.shipping_type.enable'),
      dataIndex: 'enable',
      key: 'enable',
      width: 80,
      align: 'center',
      render: (enable, record) => {
        const isPlatform = record.source === 'platform';
        const switchEl = (
          <Switch
            checked={enable}
            size="small"
            disabled={isPlatform}
            onChange={() => handleToggleEnable(record)}
          />
        );
        return isPlatform ? (
          <Tooltip title={t('operator.pages.master_data.shipping_types.platform_not_toggleable')}>
            {switchEl}
          </Tooltip>
        ) : switchEl;
      }
    },
    {
      title: t('common.actions'),
      key: 'actions',
      width: 80,
      render: (_, record) => (
        !record.is_system ? (
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
        ) : null
      ),
    },
  ], [t]);

  // ── Render ──

  return (
    <div className="page-container">
      <ListPageHeader
        items={['shipping_settings', 'shipping_settings.shipping_types']}
        actions={
          canCreateCustom ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddCustom}>
              {t('operator.pages.master_data.shipping_types.add_custom')}
            </Button>
          ) : null
        }
      />
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Input.Search
            placeholder={t('common.placeholder.search_keyword')}
            onSearch={handleSearch}
            allowClear
            style={{ width: 300 }}
          />
        </div>
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={{ showSizeChanger: true }}
        />
      </Card>

      {/* 新增/編輯自訂物流 */}
      <Modal
        title={editingRecord
          ? t('operator.pages.master_data.shipping_types.edit_custom')
          : t('operator.pages.master_data.shipping_types.add_custom')
        }
        open={isModalVisible}
        onOk={handleModalSubmit}
        onCancel={handleModalCancel}
        confirmLoading={saving}
      >
        <Form form={form} layout="vertical">
          {!editingRecord && (
            <Form.Item
              name="code"
              label={t('models.shipping_type.code')}
              extra={t('operator.pages.master_data.shipping_types.code_hint')}
              rules={[{ required: true, message: t('common.validation.required') }]}
            >
              <Input addonBefore={CUSTOM_PREFIX} />
            </Form.Item>
          )}
          <Form.Item
            name="name"
            label={t('models.shipping_type.name')}
            rules={[{ required: true, message: t('common.validation.required') }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>

    </div>
  );
};

export default ShippingTypesList;
